import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ClipboardCheck,
  BarChart2,
  Users,
  Settings,
  Bell,
  ChevronRight,
  Check,
  AlertTriangle,
  FileText,
  Award,
  X,
  Anchor,
  Shield,
  BookOpen,
  ChevronDown,
  Camera,
  Crosshair,
} from 'lucide-react';
import { Link } from 'react-router-dom';

// --- Types ---
interface ChecklistItem {
  id: string;
  label: string;
  status: 'pass' | 'warning' | 'fail' | 'pending';
}

interface PhotoEvidence {
  id: string;
  title: string;
  src: string;
  status: 'Verified' | 'Inspected' | 'Needs Review';
  timestamp?: string;
  hasAnnotations?: boolean;
  description: string;
}

// --- Data ---
const initialChecklist: ChecklistItem[] = [
  { id: 'c1', label: 'Container twist-locks secured?', status: 'pass' },
  { id: 'c2', label: 'Mid-lashings applied?', status: 'pass' },
  { id: 'c3', label: 'Base lashing secured?', status: 'pass' },
  { id: 'c4', label: 'Non-slip padding installed?', status: 'pass' },
  { id: 'c5', label: 'Correct wire size?', status: 'pass' },
  { id: 'c6', label: 'D-ring integrity?', status: 'warning' },
  { id: 'c7', label: 'Weather forecast review?', status: 'pass' },
  { id: 'c8', label: 'Structural integrity inspection?', status: 'pass' },
];

const photoEvidence: PhotoEvidence[] = [
  {
    id: 'p1',
    title: 'Photo 1',
    src: '/assets/images/insp_photo_1.png',
    status: 'Verified',
    timestamp: '14:15 UTC',
    description: 'Secure Lashing: Twist-locks and Lashing Bars, Container Stacks',
  },
  {
    id: 'p2',
    title: 'Photo 2',
    src: '/assets/images/insp_photo_2.png',
    status: 'Inspected',
    hasAnnotations: true,
    description: 'Corner Casting connection',
  },
  {
    id: 'p3',
    title: 'Photo 3',
    src: '/assets/images/insp_photo_3.png',
    status: 'Needs Review',
    description: 'Winch wire on flat rack',
  },
];

// --- Sub-components ---
const StatusBadge: React.FC<{ status: PhotoEvidence['status'] }> = ({ status }) => {
  const config = {
    Verified: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    Inspected: 'bg-sky-500/20 text-sky-400 border-sky-500/30',
    'Needs Review': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  };
  return (
    <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${config[status]}`}>
      {status}
    </span>
  );
};

const CheckIcon: React.FC<{ status: ChecklistItem['status'] }> = ({ status }) => {
  if (status === 'pass') {
    return (
      <div className="w-6 h-6 rounded-md bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
        <Check className="w-3.5 h-3.5 text-emerald-400" />
      </div>
    );
  }
  if (status === 'warning') {
    return (
      <div className="w-6 h-6 rounded-md bg-amber-500/20 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
        <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
      </div>
    );
  }
  if (status === 'fail') {
    return (
      <div className="w-6 h-6 rounded-md bg-red-500/20 border border-red-500/40 flex items-center justify-center flex-shrink-0">
        <X className="w-3.5 h-3.5 text-red-400" />
      </div>
    );
  }
  return (
    <div className="w-6 h-6 rounded-md border border-white/10 flex-shrink-0" />
  );
};

// --- Main Page ---
const InspectionPro: React.FC = () => {
  const [checklist, setChecklist] = useState<ChecklistItem[]>(initialChecklist);
  const [expandedPhoto, setExpandedPhoto] = useState<string | null>('p1');
  const [activeNav, setActiveNav] = useState('inspections');

  const toggleItem = (id: string) => {
    setChecklist(prev =>
      prev.map(item => {
        if (item.id !== id) return item;
        const cycle: ChecklistItem['status'][] = ['pass', 'warning', 'fail', 'pending'];
        const nextIdx = (cycle.indexOf(item.status) + 1) % cycle.length;
        return { ...item, status: cycle[nextIdx] };
      })
    );
  };

  const passCount = checklist.filter(i => i.status === 'pass').length;
  const completionPct = Math.round((passCount / checklist.length) * 100);
  const warningItems = checklist.filter(i => i.status === 'warning');

  const navItems = [
    { id: 'inspections', label: 'Inspections', icon: ClipboardCheck },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'analytics', label: 'Analytics', icon: BarChart2 },
    { id: 'team', label: 'Team', icon: Users },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  const bottomNavItems = [
    { id: 'lashing', label: 'Lashing', icon: Anchor },
    { id: 'safety', label: 'Safety', icon: Shield },
    { id: 'docs', label: 'Reports', icon: BookOpen },
  ];

  return (
    <div
      className="min-h-screen flex"
      style={{
        background: 'linear-gradient(135deg, #0b1220 0%, #0f1a2e 50%, #111827 100%)',
        backgroundImage: `
          linear-gradient(135deg, #0b1220 0%, #0f1a2e 50%, #111827 100%),
          url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' stroke='%23ffffff' stroke-width='0.3' stroke-opacity='0.04'%3E%3Crect x='10' y='10' width='40' height='40'/%3E%3Cline x1='0' y1='30' x2='60' y2='30'/%3E%3Cline x1='30' y1='0' x2='30' y2='60'/%3E%3C/g%3E%3C/svg%3E")
        `,
      }}
    >
      {/* Background glow blobs */}
      <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
        <div className="absolute top-[-20%] right-[-10%] w-[40%] h-[60%] bg-orange-600/5 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-20%] left-[-10%] w-[40%] h-[60%] bg-blue-700/5 rounded-full blur-[120px]" />
      </div>

      {/* ======================== SIDEBAR ======================== */}
      <aside
        className="relative z-20 flex flex-col w-56 flex-shrink-0 h-screen sticky top-0"
        style={{
          background: 'rgba(10, 15, 30, 0.85)',
          backdropFilter: 'blur(20px)',
          borderRight: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        {/* Logo */}
        <div className="px-5 pt-6 pb-8">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-700 rounded-lg flex items-center justify-center shadow-lg shadow-orange-500/20 flex-shrink-0">
              <span className="text-white font-black text-sm">I</span>
            </div>
            <span className="text-white font-bold text-base tracking-tight">
              Inspection<span className="text-orange-500">Pro</span>
            </span>
          </div>
        </div>

        {/* Top Nav */}
        <nav className="flex-1 px-3 space-y-1">
          {navItems.map(item => {
            const Icon = item.icon;
            const active = activeNav === item.id;
            return (
              <button
                key={item.id}
                onClick={() => setActiveNav(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 text-left ${
                  active
                    ? 'bg-orange-500/15 text-white border border-orange-500/20'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-orange-400' : ''}`} />
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* Bottom Nav Tags */}
        <div className="px-3 pb-4 space-y-1 border-t border-white/5 pt-4">
          {bottomNavItems.map(item => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm text-slate-500 hover:text-slate-300 hover:bg-white/5 transition-all duration-200 text-left"
              >
                <Icon className="w-4 h-4 text-orange-500/60 flex-shrink-0" />
                {item.label}
              </button>
            );
          })}
        </div>

        {/* User Profile */}
        <div className="px-3 pb-5 pt-2 border-t border-white/5">
          <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/5 cursor-pointer transition-all">
            <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 border-2 border-orange-500/30">
              <div className="w-full h-full bg-gradient-to-br from-sky-500 to-blue-700 flex items-center justify-center">
                <span className="text-xs font-bold text-white">SC</span>
              </div>
            </div>
            <div className="min-w-0">
              <div className="text-xs font-semibold text-white truncate">Captain</div>
              <div className="text-xs text-slate-500 truncate">Sarah Chen</div>
            </div>
          </div>
        </div>
      </aside>

      {/* ======================== MAIN CONTENT ======================== */}
      <div className="relative z-10 flex-1 flex flex-col min-h-screen overflow-hidden">

        {/* ---- Header ---- */}
        <header
          className="flex items-center justify-between px-6 py-4 flex-shrink-0"
          style={{
            background: 'rgba(10, 15, 30, 0.6)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(255,255,255,0.05)',
          }}
        >
          <div className="flex flex-col">
            <span className="text-slate-500 text-xs font-medium uppercase tracking-widest">
              SaaS UI Digital Dashboard
            </span>
            <div className="flex items-center gap-3 mt-0.5">
              <h1 className="text-white text-xl font-black tracking-tight">
                LASHING SURVEY REPORT — <span className="text-orange-400">#45098</span>
              </h1>
              <span className="text-xs font-bold text-amber-400 bg-amber-400/10 border border-amber-400/20 px-2 py-0.5 rounded-full">
                IN PROGRESS
              </span>
            </div>
            <span className="text-slate-500 text-xs mt-0.5">Real-time data</span>
          </div>

          <div className="flex items-center gap-3">
            {/* Date/Time */}
            <div className="hidden lg:block text-slate-400 text-sm font-medium">
              {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              {' | '}
              {new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} UTC
            </div>

            {/* Keyboard shortcut indicator */}
            <div className="hidden lg:flex items-center gap-1 px-2 py-1 rounded-lg border border-white/10 text-slate-400 text-xs">
              <span>⌘</span><span>K</span>
            </div>

            {/* Notification */}
            <button className="relative w-9 h-9 rounded-xl border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/5 transition-all">
              <Bell className="w-4 h-4" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-orange-500 rounded-full" />
            </button>

            {/* User Avatar */}
            <div className="flex items-center gap-2.5 px-3 py-1.5 rounded-xl border border-white/10 hover:bg-white/5 cursor-pointer transition-all">
              <div className="w-7 h-7 rounded-full overflow-hidden border border-orange-500/30">
                <div className="w-full h-full bg-gradient-to-br from-sky-500 to-blue-700 flex items-center justify-center">
                  <span className="text-xs font-bold text-white">SC</span>
                </div>
              </div>
              <span className="text-sm font-medium text-white hidden sm:block">Captain Sarah Chen</span>
            </div>

            {/* Report Ready CTA */}
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              className="hidden xl:flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-sm text-white transition-all"
              style={{
                background: 'linear-gradient(135deg, #16a34a, #15803d)',
                boxShadow: '0 4px 20px rgba(22, 163, 74, 0.3)',
              }}
            >
              <Check className="w-4 h-4" />
              REPORT READY FOR CERTIFICATION
            </motion.button>
          </div>
        </header>

        {/* ---- 3-Column Body ---- */}
        <div className="flex-1 flex gap-0 overflow-hidden">

          {/* === COL 1: Cargo & Lashing Details === */}
          <div
            className="flex flex-col w-80 flex-shrink-0 overflow-y-auto"
            style={{ borderRight: '1px solid rgba(255,255,255,0.05)' }}
          >
            {/* Panel header */}
            <div className="px-5 pt-5 pb-3 flex items-center justify-between flex-shrink-0">
              <h2 className="text-[11px] font-black text-white uppercase tracking-widest">
                Cargo &amp; Lashing Details
              </h2>
              <div
                className="flex items-center gap-1. px-2.5 py-1 rounded-lg text-xs font-semibold text-amber-400 cursor-pointer"
                style={{ background: 'rgba(251, 191, 36, 0.1)', border: '1px solid rgba(251,191,36,0.2)' }}
              >
                High-risk checklist <ChevronDown className="w-3 h-3 ml-1" />
              </div>
            </div>

            {/* Cargo info card */}
            <div className="mx-4 mb-4 rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-5 h-5 rounded bg-orange-500 flex items-center justify-center flex-shrink-0">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-orange-400 text-xs font-black uppercase tracking-widest">General Cargo: Containers</span>
              </div>
              <div className="space-y-1 text-xs text-slate-400">
                <div>Container ID: <span className="text-slate-200 font-medium">ABCU1234567</span></div>
                <div>Vessel: <span className="text-slate-200 font-medium">MV Global Trade</span></div>
                <div>Location: <span className="text-slate-200 font-medium">Port of Hamburg</span></div>
              </div>
            </div>

            {/* Photo Evidence List */}
            <div className="flex-1 px-4 space-y-3 pb-5">
              {photoEvidence.map(photo => (
                <motion.div
                  key={photo.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl overflow-hidden cursor-pointer group"
                  style={{
                    background: expandedPhoto === photo.id
                      ? 'rgba(255,165,0,0.05)'
                      : 'rgba(255,255,255,0.04)',
                    border: expandedPhoto === photo.id
                      ? '1px solid rgba(249,115,22,0.25)'
                      : '1px solid rgba(255,255,255,0.07)',
                    transition: 'all 0.25s ease',
                  }}
                  onClick={() => setExpandedPhoto(expandedPhoto === photo.id ? null : photo.id)}
                >
                  <div className="flex items-center justify-between px-4 py-3">
                    <span className="text-sm font-bold text-white">{photo.title}</span>
                    <ChevronRight
                      className="w-4 h-4 text-slate-500 group-hover:text-slate-300 transition-all"
                      style={{ transform: expandedPhoto === photo.id ? 'rotate(90deg)' : 'rotate(0deg)' }}
                    />
                  </div>

                  <AnimatePresence>
                    {expandedPhoto === photo.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 space-y-3">
                          {/* Image */}
                          <div className="relative rounded-xl overflow-hidden aspect-[4/3]">
                            <img
                              src={photo.src}
                              alt={photo.description}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                            <button className="absolute top-2 right-2 w-7 h-7 rounded-lg bg-black/50 backdrop-blur-sm flex items-center justify-center">
                              <Camera className="w-3.5 h-3.5 text-white" />
                            </button>
                          </div>

                          {/* Meta */}
                          <div className="flex items-center justify-between">
                            <div className="space-y-1">
                              <div className="flex items-center gap-1.5">
                                <span className="text-xs text-slate-500">Status</span>
                                <StatusBadge status={photo.status} />
                              </div>
                              {photo.timestamp && (
                                <div className="text-xs text-slate-500">
                                  Timestamp: <span className="text-slate-300">{photo.timestamp}</span>
                                </div>
                              )}
                              {photo.hasAnnotations && (
                                <div className="flex items-center gap-1 text-xs text-slate-400">
                                  <Crosshair className="w-3 h-3" /> Annotations
                                </div>
                              )}
                            </div>
                          </div>
                          <p className="text-xs text-slate-400 leading-relaxed">{photo.description}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}

              {/* Add Photo Button */}
              <button
                className="w-full py-3 rounded-2xl text-sm font-semibold text-slate-500 hover:text-slate-300 transition-all flex items-center justify-center gap-2"
                style={{ border: '1px dashed rgba(255,255,255,0.1)' }}
              >
                <Camera className="w-4 h-4" />
                Add Evidence Photo
              </button>
            </div>
          </div>

          {/* === COL 2: Technical Checklist === */}
          <div
            className="flex flex-col flex-1 overflow-y-auto"
            style={{ borderRight: '1px solid rgba(255,255,255,0.05)' }}
          >
            <div className="px-6 pt-5 pb-3 flex items-center justify-between flex-shrink-0">
              <h2 className="text-[11px] font-black text-white uppercase tracking-widest">Technical Checklist</h2>
              <div
                className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-slate-400 cursor-pointer hover:text-slate-200 transition-all"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
              >
                Auto-checklist <ChevronDown className="w-3 h-3 ml-1" />
              </div>
            </div>

            <div className="flex-1 px-6 pb-6 space-y-2.5">
              {checklist.map((item, i) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-3 group"
                >
                  {/* Left empty checkbox (decorative) */}
                  <div className="w-4 h-4 rounded border border-white/10 flex-shrink-0" />

                  {/* Label */}
                  <span
                    className="flex-1 text-sm text-slate-300 group-hover:text-white transition-colors cursor-default"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {item.label}
                  </span>

                  {/* Status icon (clickable to toggle) */}
                  <button
                    onClick={() => toggleItem(item.id)}
                    className="flex-shrink-0 hover:scale-110 transition-transform"
                    title="Click to cycle status"
                  >
                    <CheckIcon status={item.status} />
                  </button>
                </motion.div>
              ))}
            </div>

            {/* Progress Bar */}
            <div
              className="mx-6 mb-6 p-4 rounded-2xl flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs text-slate-400 font-medium">Checklist Progress</span>
                <span className="text-xs font-bold text-orange-400">{passCount}/{checklist.length} items</span>
              </div>
              <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: 'linear-gradient(90deg, #f97316, #ea580c)' }}
                  initial={{ width: 0 }}
                  animate={{ width: `${completionPct}%` }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                />
              </div>
              <div className="text-right text-xs text-slate-500 mt-1">{completionPct}% Complete</div>
            </div>
          </div>

          {/* === COL 3: Summary & Recommendations === */}
          <div className="flex flex-col w-80 flex-shrink-0 overflow-y-auto">
            <div className="px-5 pt-5 pb-3 flex-shrink-0">
              <h2 className="text-[11px] font-black text-white uppercase tracking-widest">Summary &amp; Recommendations</h2>
            </div>

            {/* Summary Card */}
            <div
              className="mx-4 mb-4 rounded-2xl p-4"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              {/* Issues */}
              <div className="mb-4">
                <h3 className="text-xs font-black text-white uppercase tracking-widest mb-2">Issues</h3>
                <ul className="space-y-1.5">
                  <li className="text-xs text-slate-400 leading-relaxed">
                    Container twist-locks secured? <span className="text-slate-200">Secured</span>
                  </li>
                  <li className="text-xs text-slate-400 leading-relaxed">
                    Mid-lashings applied? <span className="text-slate-200">All green, checked</span>
                  </li>
                  <li className="text-xs text-slate-400 leading-relaxed">
                    Base lashing secured? <span className="text-slate-200">Non-slip padded</span>
                  </li>
                </ul>
              </div>

              {/* Notes */}
              <div>
                <h3 className="text-xs font-black text-white uppercase tracking-widest mb-2">Notes</h3>
                <ul className="space-y-1.5">
                  <li className="text-xs text-slate-400 leading-relaxed">
                    Correct wire size? <span className="text-slate-200">Secure, twist-locks and lashing bars loo container stacks</span>
                  </li>
                  <li className="text-xs text-slate-400 leading-relaxed">
                    Weather integrity inspection? <span className="text-slate-200">Review of the field</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Warnings */}
            {warningItems.length > 0 && (
              <div
                className="mx-4 mb-4 rounded-2xl p-4"
                style={{ background: 'rgba(251,191,36,0.05)', border: '1px solid rgba(251,191,36,0.15)' }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400" />
                  <span className="text-xs font-bold text-amber-400 uppercase tracking-widest">Attention Required</span>
                </div>
                {warningItems.map(w => (
                  <p key={w.id} className="text-xs text-amber-300/70 mt-1">{w.label}</p>
                ))}
              </div>
            )}

            {/* Issue Certificate CTA */}
            <div className="mx-4 mb-4 flex-shrink-0">
              <motion.button
                whileHover={{ scale: 1.02, boxShadow: '0 8px 32px rgba(249,115,22,0.4)' }}
                whileTap={{ scale: 0.98 }}
                className="w-full py-4 px-5 rounded-2xl flex items-center gap-3 group"
                style={{
                  background: 'linear-gradient(135deg, #ea580c, #c2410c)',
                  boxShadow: '0 4px 24px rgba(234, 88, 12, 0.3)',
                }}
              >
                <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                  <Award className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <div className="text-white font-black text-base leading-none">ISSUE CERTIFICATE</div>
                  <div className="text-orange-200/70 text-xs mt-1 leading-none">Preview &amp; Generate Digital Certificate</div>
                </div>
              </motion.button>
            </div>

            {/* Completion Summary */}
            <div
              className="mx-4 mb-4 rounded-2xl p-4 flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <div className="flex justify-between items-center mb-2">
                <span className="text-xs font-bold text-white">Summary</span>
                <span className="text-xs font-bold text-orange-400">{completionPct}% Complete</span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden mb-3" style={{ background: 'rgba(255,255,255,0.08)' }}>
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: 'linear-gradient(90deg, #f97316, #ea580c)' }}
                  animate={{ width: `${completionPct}%` }}
                  transition={{ duration: 0.6, ease: 'easeOut' }}
                />
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                Secure twist-locks secured. Issue or center lashing stacks note: to preserve rate as contain.
              </p>
            </div>

            {/* Back to Hub */}
            <div className="px-4 pb-5 mt-auto">
              <Link
                to="/"
                className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-300 transition-colors"
              >
                ← Back to LashingPlan Hub
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InspectionPro;
